import { useQuery } from '@tanstack/react-query';
import { apiGet } from '@/api/client';
import { useAuthStore } from '@/store/authStore';
import { NOT_LIMITED, PENDING_ACCESS, parseLimitedAccess, type LimitedAccess } from '@/lib/limitedAccess';

export interface LimitedAccessState {
  access: LimitedAccess;
  /** The active site's answer is not in yet: show the restricted set and hold the page. */
  pending: boolean;
  /** The active site's answer could not be read (still restricted; offer Try again). */
  failed: boolean;
  retry: () => void;
}

/**
 * The sign-in answer applies only to the site it names. Pure, so the rule is tested on its own.
 *   - no active site yet, or the active site is the one the sign-in answered for: that answer;
 *   - another site: its own answer once in, else PENDING_ACCESS (fail closed, never the primary's).
 */
export function resolveLimitedAccess(input: {
  fromIdentity: LimitedAccess | null | undefined;
  activeSiteId: string | null;
  siteAnswer: LimitedAccess | undefined;
  failed: boolean;
}): { access: LimitedAccess; pending: boolean; failed: boolean } {
  const identity = input.fromIdentity ?? NOT_LIMITED;
  if (!input.activeSiteId || identity.site_id === input.activeSiteId) {
    return { access: identity, pending: false, failed: false };
  }
  if (input.siteAnswer) return { access: input.siteAnswer, pending: false, failed: false };
  return { access: PENDING_ACCESS, pending: true, failed: input.failed };
}

/** Only ask again when the sign-in answer is for a different site than the one being edited. */
export function needsSiteLookup(fromIdentity: LimitedAccess | null | undefined, activeSiteId: string | null): boolean {
  return !!activeSiteId && (fromIdentity ?? NOT_LIMITED).site_id !== activeSiteId;
}

/**
 * This person's limits on the site being edited (lib/limitedAccess.ts), with the loading state.
 *
 * GET /auth/me at sign-in answers for one site (it says which). When another site is active
 * (X-Site-Id), the gateway is asked again for that site: one account can hold a managed site and
 * one it built itself. Until that answer is in, the dashboard stays restricted.
 */
export function useLimitedAccessState(): LimitedAccessState {
  const fromIdentity = useAuthStore((s) => s.limitedAccess);
  const activeSiteId = useAuthStore((s) => s.activeSiteId);
  const userId = useAuthStore((s) => s.userId);
  const lookup = needsSiteLookup(fromIdentity, activeSiteId);
  const { data, isError, refetch } = useQuery({
    queryKey: ['limited-access', userId, activeSiteId],
    queryFn: async () => parseLimitedAccess((await apiGet<{ limited_access?: unknown }>('/auth/me')).limited_access),
    enabled: lookup && !!userId,
    staleTime: 30_000,
  });
  const resolved = resolveLimitedAccess({ fromIdentity, activeSiteId, siteAnswer: lookup ? data : undefined, failed: isError });
  return { ...resolved, retry: () => void refetch() };
}

/** Just the access (restricted while the active site's answer is pending). */
export function useLimitedAccess(): LimitedAccess {
  return useLimitedAccessState().access;
}
